import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Row,
  Col,
  Spin,
  message,
  Alert,
  Descriptions,
  Image,
  Space,
  Divider,
  Form,
  TimePicker,
  Modal,
  Rate,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { customerAPI } from '../services/api';
import BookingTimeline from '../components/BookingTimeline';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);

  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [rating, setRating] = useState(0);

  const handleSubmitRating = (values) => {
    const payload = {
      bookingId: id,
      rating,
      comments: values.comments || "",
    };
    console.log("Submitting rating:", payload);

    // Call API here -> customerAPI.submitRating(payload)
    message.success("Thanks for your feedback!");
    setRatingModalOpen(false);
  };


  useEffect(() => {
    fetchBookingDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBookingDetail = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getBookingDetail(id);

      if (response.success) {
        setBooking(response.data);
      } else {
        message.error('Failed to fetch booking details');
        navigate('/bookings');
      }
    } catch (error) {
      console.error('Error fetching booking detail:', error);
      message.error('Failed to fetch booking details');
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'green',
      in_progress: 'orange',
      completed: 'blue',
      cancelled: 'red',
      settled: 'purple',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'green',
      pending: 'orange',
      failed: 'red',
      refunded: 'blue',
    };
    return colors[status] || 'default';
  };

  // ✅ Update booking details
  const handleUpdateBooking = async (values) => {
    try {
      setUpdating(true); // show loading in button
      const payload = {
        pickupTime: values.pickupTime.format("HH:mm"),
        returnTime: values.returnTime.format("HH:mm"),
      };

      await customerAPI.updateBooking(id, payload);
      message.success("Booking updated successfully!");

      // Optimistically update local state
      setBooking((prev) => ({
        ...prev,
        pickupTime: payload.pickupTime,
        returnTime: payload.returnTime,
      }));

      setIsModalOpen(false);
      fetchBookingDetail(); // refresh from backend
    } catch (err) {
      console.error(err);
      message.error("Failed to update booking");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px 24px',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 24
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/bookings')}
            style={{ marginRight: 16 }}
          >
            Back to Bookings
          </Button>
          <img
            src="/logo.png"
            alt="Xoom Drives"
            style={{ height: 40 }}
          />
        </div>
      </div>

      <div className="container">

        <div className="two-column-layout">
          {/* Left Column - Main Content */}
          <div>
            {/* Status Alert */}
            {/* {booking.bookingStatus === 'confirmed' && (
              <Alert
                message="Your Booking is on Hold"
                description={`We held your booking until ${new Date(booking.pickupDate).toLocaleDateString()} at ${booking.pickupTime}. If you reserve change, we will get back to you.`}
                type="warning"
                showIcon
                style={{ marginBottom: 24, borderRadius: 8 }}
              />
            )} */}

            {booking.bookingStatus === 'completed' && (
              <Alert
                message="Congratulations! We have sent your book details to the vehicle owner."
                type="success"
                showIcon
                style={{ marginBottom: 24, borderRadius: 8 }}
              />
            )}


            {/* Vehicle Details */}
            <Card title="Vehicle Details" style={{ marginBottom: 24 }} className="main-card">
              <Row gutter={[24, 16]}>
                <Col xs={24} md={8}>
                  {booking.vehicle.vehicleImageUrls && booking.vehicle.vehicleImageUrls.length > 0 && (
                    <Image
                      width="100%"
                      src={booking.vehicle.vehicleImageUrls[0]}
                      alt={`${booking.vehicle.make} ${booking.vehicle.model}`}
                      style={{ borderRadius: 8 }}
                    />
                  )}
                </Col>
                <Col xs={24} md={16}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        {booking.vehicle.make} {booking.vehicle.model} ({booking.vehicle.year})
                      </Title>
                      <Text type="secondary">Registration: {booking.vehicle.registrationNumber}</Text>
                    </div>

                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Text strong>Color:</Text>
                        <br />
                        <Text>{booking.vehicle.color}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>Body Type:</Text>
                        <br />
                        <Text>{booking.vehicle.bodyType}</Text>
                      </Col>
                    </Row>

                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Text strong>Fuel Type:</Text>
                        <br />
                        <Text>{booking.vehicle.fuelType}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>Transmission:</Text>
                        <br />
                        <Text>{booking.vehicle.transmission}</Text>
                      </Col>
                    </Row>


                    <div>
                      <Text strong>Price per Day:</Text>
                      <br />
                      <Text style={{ fontSize: '18px', color: '#2e5ec3', fontWeight: 600 }}>
                        AED {parseFloat(booking.vehicle.pricePerDayAED).toFixed(2)}
                      </Text>
                    </div>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* summary */}
            <Card title="Trip Details" style={{ marginBottom: 24 }} className="summary-card"
              extra={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    type="primary"
                    variant='outlined'
                    onClick={() => {
                      setIsModalOpen(true);
                      form.setFieldsValue({
                        pickupTime: dayjs(booking.pickupTime, "HH:mm"),
                        returnTime: dayjs(booking.returnTime, "HH:mm"),
                      });
                    }}
                    // onClick={onPayNow}
                    style={{
                      backgroundColor: '#2e5ec3', // Primary color
                      borderColor: '#2e5ec3', // Match border to primary color
                    }}

                  >
                    Edit
                  </Button>
                </div>

              }>
              <div className="summary-row">
                <Text>Total Vehicles</Text>
                <Text strong>1 Vehicle</Text>
              </div>

              <div className="summary-row">
                <Text>Pickup Location</Text>
                <Text strong>{booking.pickupLocation}</Text>
              </div>

              <div className="summary-row">
                <Text>Pickup Date</Text>
                <Text strong>
                  {new Date(booking.pickupDate).toLocaleDateString()}, {booking.pickupTime}
                </Text>
              </div>

              <div className="summary-row">
                <Text>Return Date</Text>
                <Text strong>
                  {new Date(booking.returnDate).toLocaleDateString()}, {booking.returnTime}
                </Text>
              </div>


            </Card>

            {/* summary */}
            <Card title="Price Details" style={{ marginBottom: 24 }} className="summary-card" extra={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Button
                  type="primary"
                  // onClick={onPayNow}
                  style={{
                    backgroundColor: '#2e5ec3', // Primary color
                    borderColor: '#2e5ec3', // Match border to primary color
                  }}
                >
                  Pay Now
                </Button>
              </div>
            }>


              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Price Details</Title>
              </div>

              <div className="summary-row">
                <Text>Base Amount</Text>
                <Text>AED {parseFloat(booking.baseAmount).toFixed(2)}</Text>
              </div>

              {booking.platformCommission > 0 && (
                <div className="summary-row">
                  <Text>Platform Fee</Text>
                  <Text>AED {parseFloat(booking.platformCommission).toFixed(2)}</Text>
                </div>
              )}

              <div className="summary-row">
                <Text>Duration</Text>
                <Text>{booking.totalDays} days</Text>
              </div>

              {booking.securityDeposit > 0 && (
                <div className="summary-row">
                  <Text>Security Deposit</Text>
                  <Text>AED {parseFloat(booking.securityDeposit).toFixed(2)}</Text>
                </div>
              )}

              <div className="summary-total">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 18 }}>Total</Text>
                  <Text strong style={{ fontSize: 18, color: '#2e5ec3' }}>
                    AED {parseFloat(booking.totalAmount).toFixed(2)}
                  </Text>
                </div>
              </div>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Status: </Text>
                  <Tag color={getStatusColor(booking.bookingStatus)}>
                    {booking.bookingStatus.toUpperCase()}
                  </Tag>
                </div>
                <div>
                  <Text strong>Payment: </Text>
                  <Tag color={getPaymentStatusColor(booking.paymentStatus)}>
                    {booking.paymentStatus.toUpperCase()}
                  </Tag>
                </div>
                <div>
                  <Text strong>Booking Number: </Text>
                  <Text code>{booking.bookingNumber}</Text>
                </div>
                {booking.specialRequests && (
                  <div>
                    <Text strong>Special Requests: </Text>
                    <Text>{booking.specialRequests}</Text>
                  </div>
                )}
              </Space>
            </Card>


            {/* Book Information */}
            <Card title="Book Information" style={{ marginBottom: 24 }} className="main-card">
              <Row gutter={[24, 16]}>
                <Col xs={24} md={8}>
                  <div>
                    <Text strong>Full Name:</Text>
                    <br />
                    <Text>{booking.customerName}</Text>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div>
                    <Text strong>Email:</Text>
                    <br />
                    <Text>{booking.customerEmail || 'N/A'}</Text>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div>
                    <Text strong>Phone Number:</Text>
                    <br />
                    <Text>{booking.customerPhone}</Text>
                  </div>
                </Col>
              </Row>
            </Card>



            {/* Vendor Information */}
            <Card title="Vendor Information" style={{ marginBottom: 24 }} className="main-card">
              <Descriptions column={1} bordered={false}>
                <Descriptions.Item label="Company Name">
                  {booking.vendor.companyName}
                </Descriptions.Item>
                <Descriptions.Item label={<><MailOutlined /> Contact Email</>}>
                  <a href={`mailto:${booking.vendor.contactEmail}`}>
                    {booking.vendor.contactEmail}
                  </a>
                </Descriptions.Item>
                <Descriptions.Item label={<><PhoneOutlined /> Contact Phone</>}>
                  <a href={`tel:${booking.vendor.contactPhone}`}>
                    {booking.vendor.contactPhone}
                  </a>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Tasks Information */}
            {(booking.pickupTasks?.length > 0 || booking.deliveryTasks?.length > 0) && (
              <Card title="Task Information" className="main-card">
                {booking.pickupTasks?.map((task) => (
                  <div key={task.id} style={{ marginBottom: 16 }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <div>
                          <Text strong>{task.title}</Text>
                          <br />
                          <Text type="secondary">
                            <EnvironmentOutlined /> {task.taskLocation}
                          </Text>
                        </div>
                      </Col>
                      <Col>
                        <Tag color={getStatusColor(task.status)}>
                          {task.status.toUpperCase()}
                        </Tag>
                      </Col>
                    </Row>
                    {task.assignedToUser && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                        <Text type="secondary">
                          Assigned to: {task.assignedToUser.name} ({task.assignedToUser.phoneNumber})
                        </Text>
                      </div>
                    )}
                  </div>
                ))}

                {booking.deliveryTasks?.map((task) => (
                  <div key={task.id}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <div>
                          <Text strong>{task.title}</Text>
                          <br />
                          <Text type="secondary">
                            <EnvironmentOutlined /> {task.taskLocation}
                          </Text>
                        </div>
                      </Col>
                      <Col>
                        <Tag color={getStatusColor(task.status)}>
                          {task.status.toUpperCase()}
                        </Tag>
                      </Col>
                    </Row>
                    {task.assignedToUser && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                        <Text type="secondary">
                          Assigned to: {task.assignedToUser.name} ({task.assignedToUser.phoneNumber})
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
              </Card>
            )}
          </div>

          {/* Right Column - Summary */}
          <div>


          <Button
  block
  type="primary"
  icon={<StarFilled style={{ color: "#faad14" }} />}
  onClick={() => setRatingModalOpen(true)}
  style={{ marginBottom: "16px" }}
>
  Rate My Experience
</Button>


            {/* Booking Timeline */}
            <BookingTimeline bookingId={id} />

          </div>
        </div>
      </div>
      <Modal
        title="Edit trip details"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateBooking}
        >
          <Form.Item
            label="Pickup Time"
            name="pickupTime"
            rules={[{ required: true, message: 'Please select pickup time' }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Drop-off Time"
            name="returnTime"
            rules={[{ required: true, message: 'Please select drop-off time' }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
            <Button variant='outlined' color='primary' onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updating} // ⬅️ loading state
            >
              Yes
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={null}
        open={ratingModalOpen}
        onCancel={() => setRatingModalOpen(false)}
        footer={null}
        centered
      >
        <div style={{ textAlign: "center" }}>
          <h3 style={{ marginBottom: "16px" }}>Give this booking a star rating</h3>
          <Rate
            value={rating}
            onChange={setRating}
            style={{ fontSize: 32, marginBottom: 16 }}
          />

          <Form layout="vertical" onFinish={handleSubmitRating}>
            <Form.Item label="Comments (Optional)" name="comments">
              <Input.TextArea rows={4} />
            </Form.Item>

            <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
              <Button onClick={() => setRatingModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </div>
          </Form>
        </div>
      </Modal>


    </div>
  );
};

export default BookingDetail;